import React, { useEffect, useMemo, useState } from "react";
import CtmsWorkspaceModal from "../../common/ctms/CtmsWorkspaceModal";
import { useUserData } from "../../../hooks/useUserData";
import { useLoader } from "../../../context/LoaderContext";
import {
  fetchVisitWiseStudyInfo,
  fetchVolunteerProfile,
  fetchVolunteerStudySummary,
  getVolunteerDisplayName,
  loadParticipantClinicalPack,
} from "./participantService";
import {
  formatParticipantDate,
  getParticipantAgeYears,
  getParticipantProfilePictureUrl,
} from "./participantReportUtils";

const APPOINTMENT_COLUMNS = [
  {
    key: "visit",
    label: "Visit Type",
    keys: ["VisitName", "VisitType", "visitName"],
  },
  {
    key: "start",
    label: "Start Date",
    keys: ["StartTime", "AppointmentDate", "StartDate", "startDate"],
  },
  {
    key: "status",
    label: "Status",
    keys: ["Status", "AppointmentStatus", "status"],
  },
  {
    key: "payment",
    label: "Payment",
    keys: ["Payment", "Amount", "PaidAmount"],
  },
];

const TABS = [
  { id: "details", label: "Participant Details" },
  { id: "clinical", label: "Clinical Details" },
  { id: "appointments", label: "Appointments" },
];

function pick(row, ...keys) {
  for (const key of keys) {
    const value = row?.[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return String(value).trim();
    }
  }
  return "";
}

function displayFlag(value) {
  const text = String(value ?? "").trim();
  if (!text) return "";
  if (text === "1" || /^true|yes$/i.test(text)) return "Yes";
  if (text === "0" || /^false|no$/i.test(text)) return "No";
  return text;
}

function DetailField({ label, value, breakWord = false }) {
  const text = value || "Not Available";
  return (
    <div className="pm-feas-vol-detail__field">
      <span className="pm-feas-vol-detail__label">{label}</span>
      <span
        className={`pm-feas-vol-detail__value${
          breakWord ? " is-break" : ""
        }`}
        title={text}
      >
        {text}
      </span>
    </div>
  );
}

function ClinicalSection({ title, rows, columns, emptyLabel }) {
  return (
    <section className="pm-feas-vol-detail__section">
      <h4 className="pm-feas-vol-detail__section-title">{title}</h4>
      {!rows?.length ? (
        <p className="pm-feas-vol-detail__empty">{emptyLabel}</p>
      ) : (
        <div className="pm-feas-vol-detail__table-wrap">
          <table className="pm-feas-vol-detail__table">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col.key}>{col.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={`${title}-${index}`}>
                  {columns.map((col) => {
                    const value = col.render
                      ? col.render(row)
                      : pick(row, ...(col.keys || [col.key]));
                    return (
                      <td key={col.key} title={value || "—"}>
                        {value || "—"}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/**
 * Centered participant modal from Feasibility Run Query
 * (legacy showvolunteerdetails: Details + Clinical + Appointments).
 */
export default function FeasibilityVolunteerDetailModal({
  open,
  volunteerId,
  onClose,
}) {
  const { userData } = useUserData();
  const { showLoader, hideLoader } = useLoader();
  const [tab, setTab] = useState("details");
  const [volunteer, setVolunteer] = useState(null);
  const [clinical, setClinical] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      setTab("details");
      setVolunteer(null);
      setClinical(null);
      setAppointments([]);
      setError("");
      return undefined;
    }
    if (!volunteerId || !userData?.OrganizationId) return undefined;

    let cancelled = false;
    (async () => {
      showLoader("Loading participant details...");
      setError("");
      try {
        const [profileResult, clinicalPack, studySummary] =
          await Promise.all([
            fetchVolunteerProfile(userData, volunteerId),
            loadParticipantClinicalPack(userData, volunteerId).catch(() => null),
            fetchVolunteerStudySummary(userData, volunteerId).catch(() => null),
          ]);

        if (cancelled) return;

        const row = profileResult?.volunteer ?? null;
        if (!row) {
          setVolunteer(null);
          setError("Participant details not found.");
          return;
        }
        setVolunteer(row);
        setClinical(clinicalPack);

        const studyRows = Array.isArray(studySummary?.studies)
          ? studySummary.studies
          : [];
        const studyIds = studyRows
          .map((item) => pick(item, "StudyId", "studyId"))
          .filter(Boolean);

        if (studyIds.length) {
          try {
            const visitInfo = await fetchVisitWiseStudyInfo(
              userData,
              volunteerId,
              studyIds,
            );
            if (!cancelled) {
              setAppointments(
                Array.isArray(visitInfo?.appointments)
                  ? visitInfo.appointments
                  : [],
              );
            }
          } catch {
            if (!cancelled) setAppointments([]);
          }
        } else {
          setAppointments(
            Array.isArray(studySummary?.appList) ? studySummary.appList : [],
          );
        }
      } catch (err) {
        if (!cancelled) {
          setVolunteer(null);
          setError(err?.message || "Could not load participant details.");
        }
      } finally {
        if (!cancelled) hideLoader();
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- bind only when target opens
  }, [open, volunteerId]);

  const titleName = volunteer
    ? getVolunteerDisplayName(volunteer)
    : "Participant Details";
  const photoUrl = volunteer ? getParticipantProfilePictureUrl(volunteer) : "";

  const appointmentsByStudy = useMemo(() => {
    const groups = new Map();
    for (const app of Array.isArray(appointments) ? appointments : []) {
      const studyId =
        pick(app, "StudyId", "studyId") ||
        pick(app, "StudyName", "studyName") ||
        "unknown";
      const studyName = pick(app, "StudyName", "studyName") || "Study";
      if (!groups.has(studyId)) {
        groups.set(studyId, { studyId, studyName, rows: [] });
      }
      groups.get(studyId).rows.push(app);
    }
    return [...groups.values()];
  }, [appointments]);

  const fields = useMemo(() => {
    if (!volunteer) return [];
    const age =
      pick(volunteer, "Age") ||
      (() => {
        const years = getParticipantAgeYears(volunteer.DOB);
        return years == null ? "" : String(years);
      })();
    const ethnicity = pick(
      volunteer,
      "EthenicityReason",
      "EthnicityReason",
      "EthnicityName",
      "Ethnicity",
      "ethnicity",
    );

    return [
      { label: "Participant ID", value: pick(volunteer, "VolunteerNo") },
      {
        label: "First Name",
        value: pick(volunteer, "ForeName", "FirstName"),
      },
      {
        label: "Last Name",
        value: pick(volunteer, "SurName", "LastName"),
      },
      {
        label: "Status",
        value: pick(volunteer, "VolStatus", "Status"),
      },
      {
        label: "Participant in Database",
        value: pick(volunteer, "IsUseVolunteerDB"),
      },
      {
        label: "Contact No.",
        value: pick(volunteer, "ContactNumber", "MobileNumber"),
      },
      { label: "NHS Number", value: pick(volunteer, "NHSNumber") },
      { label: "NI Number", value: pick(volunteer, "NINumber") },
      { label: "Gender", value: pick(volunteer, "Gender") },
      {
        label: "Date of Birth",
        value: formatParticipantDate(pick(volunteer, "DOB")),
      },
      { label: "Age", value: age },
      {
        label: "Work Email Address",
        value: pick(volunteer, "EmailId", "Email"),
        breakWord: true,
      },
      {
        label: "Personal email address",
        value: pick(volunteer, "PEmailId", "WorkEmailId"),
        breakWord: true,
      },
      {
        label: "Landline Number",
        value: pick(volunteer, "LandLine", "Landline"),
      },
      { label: "Ethnicity", value: ethnicity },
      {
        label: "Next of Kin Name",
        value: pick(volunteer, "KinName", "NextofKinName"),
      },
      {
        label: "Next of Kin Contact No.",
        value: pick(volunteer, "KinContactNo", "NextofKinContact"),
      },
      {
        label: "Verify NHS",
        value: displayFlag(pick(volunteer, "VerifyNHS", "IsVerifyNHS")),
      },
      {
        label: "Verify NI",
        value: displayFlag(pick(volunteer, "VerifyNI", "IsVerify")),
      },
      {
        label: "Receive SMS",
        value: displayFlag(pick(volunteer, "IsSMS")),
      },
      {
        label: "Receive Email",
        value: displayFlag(pick(volunteer, "IsEmail")),
      },
    ].map((field) => ({
      ...field,
      value:
        field.label === "Date of Birth" && field.value === "—"
          ? ""
          : field.value,
    }));
  }, [volunteer]);

  return (
    <CtmsWorkspaceModal
      open={open}
      title="Participant Details"
      titleId="pm-feas-volunteer-detail-title"
      onClose={onClose}
      className="ctms-workspace-modal--stacked ctms-workspace-modal--compact-host"
      dialogClassName="ctms-workspace-modal__dialog--feas-vol-detail"
      contentClassName="pm-feas-vol-detail-modal"
    >
      <div className="pm-feas-vol-detail">
        {error ? (
          <div className="pm-feas-vol-detail__error" role="alert">
            {error}
          </div>
        ) : null}

        {volunteer ? (
          <>
            <div className="pm-feas-vol-detail__sticky">
              <div className="pm-feas-vol-detail__hero">
                <div className="pm-feas-vol-detail__avatar" aria-hidden="true">
                  {photoUrl ? (
                    <img src={photoUrl} alt="" />
                  ) : titleName && titleName !== "—" ? (
                    titleName.charAt(0).toUpperCase()
                  ) : (
                    "?"
                  )}
                </div>
                <div className="pm-feas-vol-detail__hero-copy">
                  <h3>{titleName}</h3>
                  <p>
                    {pick(volunteer, "VolunteerNo", "volunteerNo") ||
                      "No participant ID"}
                  </p>
                </div>
              </div>

              <div className="pm-feas-vol-detail__tabs" role="tablist">
                {TABS.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    role="tab"
                    aria-selected={tab === item.id}
                    className={`pm-feas-vol-detail__tab${
                      tab === item.id ? " is-active" : ""
                    }`}
                    onClick={() => setTab(item.id)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="pm-feas-vol-detail__scroll">
            {tab === "details" ? (
              <div className="pm-feas-vol-detail__grid">
                {fields.map((field) => (
                  <DetailField
                    key={field.label}
                    label={field.label}
                    value={field.value}
                    breakWord={field.breakWord}
                  />
                ))}
              </div>
            ) : null}

            {tab === "clinical" ? (
              <div className="pm-feas-vol-detail__panels">
                <ClinicalSection
                  title="Medical History"
                  rows={clinical?.medical}
                  emptyLabel="No medical history."
                  columns={[
                    {
                      key: "condition",
                      label: "Condition",
                      keys: ["SnomedTerm", "snomedTerm"],
                    },
                    {
                      key: "onset",
                      label: "Date of Onset",
                      keys: ["DateOfOnSet", "DateOfOnset"],
                    },
                    {
                      key: "resolution",
                      label: "Resolution Date",
                      keys: ["DateOfResolution"],
                    },
                    { key: "status", label: "Status", keys: ["Status"] },
                  ]}
                />
                <ClinicalSection
                  title="Surgical History"
                  rows={clinical?.surgical}
                  emptyLabel="No surgical history."
                  columns={[
                    {
                      key: "condition",
                      label: "Condition",
                      keys: ["SnomedTerm", "snomedTerm"],
                    },
                    {
                      key: "onset",
                      label: "Date of Onset",
                      keys: ["DateOfOnSet", "DateOfOnset"],
                    },
                    {
                      key: "resolution",
                      label: "Resolution Date",
                      keys: ["DateOfResolution"],
                    },
                    { key: "status", label: "Status", keys: ["Status"] },
                  ]}
                />
                <ClinicalSection
                  title="Family History"
                  rows={clinical?.family}
                  emptyLabel="No family history."
                  columns={[
                    {
                      key: "condition",
                      label: "Condition",
                      keys: ["SnomedTerm", "snomedTerm"],
                    },
                    {
                      key: "onset",
                      label: "Date of Onset",
                      keys: ["DateOfOnSet", "DateOfOnset"],
                    },
                    {
                      key: "resolution",
                      label: "Resolution Date",
                      keys: ["DateOfResolution"],
                    },
                    { key: "status", label: "Status", keys: ["Status"] },
                  ]}
                />
                <ClinicalSection
                  title="Allergies"
                  rows={clinical?.allergy}
                  emptyLabel="No allergies."
                  columns={[
                    {
                      key: "condition",
                      label: "Condition",
                      keys: ["SnomedTerm", "snomedTerm"],
                    },
                    {
                      key: "lko",
                      label: "LKO Date",
                      keys: ["LKODate", "LkoDate"],
                    },
                    {
                      key: "resolution",
                      label: "Resolution Date",
                      keys: ["DateOfResolution"],
                    },
                    { key: "status", label: "Status", keys: ["Status"] },
                  ]}
                />
                <ClinicalSection
                  title="Physical Measurements"
                  rows={clinical?.physical}
                  emptyLabel="No physical measurements."
                  columns={[
                    { key: "age", label: "Age", keys: ["Age"] },
                    {
                      key: "height",
                      label: "Height",
                      render: (row) => {
                        const h = pick(row, "Height");
                        return h ? `${h} CM` : "";
                      },
                    },
                    {
                      key: "weight",
                      label: "Weight",
                      render: (row) => {
                        const w = pick(row, "Weight");
                        return w ? `${w} KG` : "";
                      },
                    },
                    {
                      key: "bmi",
                      label: "BMI",
                      render: (row) => {
                        const b = pick(row, "BMI");
                        return b ? `${b} KG/M²` : "";
                      },
                    },
                  ]}
                />
                <ClinicalSection
                  title="Medication History"
                  rows={clinical?.medication}
                  emptyLabel="No medication history."
                  columns={[
                    {
                      key: "name",
                      label: "Medication",
                      keys: ["medicationName", "MedicationName", "SnomedTerm"],
                    },
                    { key: "dose", label: "Dose", keys: ["Dose"] },
                    {
                      key: "indication",
                      label: "Indication",
                      keys: ["Indication"],
                    },
                    {
                      key: "frequency",
                      label: "Frequency",
                      keys: ["Frequency"],
                    },
                    {
                      key: "start",
                      label: "Start Date",
                      keys: ["StartDate"],
                    },
                    {
                      key: "end",
                      label: "End Date",
                      keys: ["ResolutionDate", "EndDate"],
                    },
                    { key: "route", label: "Route", keys: ["Route"] },
                    { key: "unit", label: "Unit", keys: ["Unit"] },
                  ]}
                />
              </div>
            ) : null}

            {tab === "appointments" ? (
              <div className="pm-feas-vol-detail__panels">
                {!appointmentsByStudy.length ? (
                  <p className="pm-feas-vol-detail__empty">
                    No appointments found.
                  </p>
                ) : (
                  appointmentsByStudy.map((group) => (
                    <ClinicalSection
                      key={group.studyId}
                      title={group.studyName}
                      rows={group.rows}
                      emptyLabel="No appointments for this study."
                      columns={APPOINTMENT_COLUMNS}
                    />
                  ))
                )}
              </div>
            ) : null}
            </div>
          </>
        ) : !error ? (
          <div className="pm-feas-vol-detail__placeholder">
            Loading participant details…
          </div>
        ) : null}
      </div>
    </CtmsWorkspaceModal>
  );
}
